import React from "react";
import { Link } from "gatsby";
import tw, { styled } from "twin.macro";
import "styled-components/macro";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import moment from "moment-timezone";
import _ from "lodash";
import { TZ } from "../constants";
import CompetitionChip from "./CompetitionChip";
import logoPlaceholder from "../images/no-image_baseball.png";

const Container = styled.div`
  ${tw`border-[6px] relative bg-white rounded z-20 mx-auto min-w-full max-w-full`}
  border-color: ${(props) => props.color};
`;

const Header = styled.div`
  ${tw`
    uppercase py-4 text-white font-bold text-xl xl:text-2xl text-center
    `}
  background-color: ${(props) => props.color};
`;

const GameList = styled.ul`
  ${tw`text-sm box-border w-full`}
`;

const GameItem = styled.li`
  ${tw`flex border-y-[1px] -mb-[1px] border-gray-200 w-full overflow-hidden`}
`;
const DateContainer = styled.div`
  ${tw`p-2 w-24  xl:w-28 flex flex-col justify-center items-center`}
`;
const Date = styled.div`
  ${tw`text-center font-bold text-4xl xl:text-5xl`}
  color: ${(props) => props.color};
`;
const Month = styled.div`
  ${tw`text-center font-bold text-[12px] xl:text-sm`}
  color: ${(props) => props.color};
`;

const TeamDiv = styled.div`
  ${tw`text-center font-bold text-[10px] xl:text-[11px] uppercase`}
  color: ${(props) => props.color};
`;
const Ticket = styled.a`
  ${tw`text-white rounded text-[10px] px-2 mt-0.5 xl:py-0.5 xl:text-xs whitespace-nowrap`}
  line-height: 16px;
  background-color: ${(props) => props.color};
`;
const ContentContainerLink = styled(Link)`
  ${tw`flex flex-1 flex-col border-l border-gray-200 p-2.5 items-start justify-center`}
`;
const OpponentContainer = styled.div`
  ${tw`flex w-full justify-around items-center mt-2 xl:mt-3`}
`;
const VersusContainer = styled.div`
  ${tw`flex flex-col items-center`}
`;
const Symbol = styled.div`
  ${tw`w-full text-xl font-bold text-gray-400 flex justify-center xl:text-3xl`}
`;
const DayTime = styled.div`
  ${tw`text-gray-400 text-center w-[90px] whitespace-nowrap text-xs sm:text-sm`}
`;
const IconContainer = styled.div`
  ${tw`min-h-[48px] min-w-[48px]`}
`;
const OpponentInfo = styled.div`
  ${tw`ml-2 flex flex-col`}
`;
const Town = styled.div`
  ${tw`text-xs text-gray-600 xl:text-sm text-ellipsis overflow-hidden whitespace-nowrap w-[100px]`}
`;
const TeamName = styled.div`
  ${tw`w-full text-gray-700 text-base xl:text-lg`}
`;
const Field = styled.div`
  ${tw`text-gray-400 uppercase text-xs mt-0.5`}
`;

const PlacholderContainer = styled.div`
  ${tw`w-10`}
`;

const UpcomingGames = ({ data, color, site, homeTeams = [] }) => {
  const teamsByName = _.keyBy(
    data.allTeams.edges,
    (edge) =>
      _.get(edge, "node.frontmatter.id", "") ||
      _.get(edge, "node.frontmatter.name", "")
  );

  const allLeagues = _.get(data, "allLeagues.edges") || [];
  const gamenodes =
    site === "dawgs" ? data.allHomeGames.nodes : data.allGames.nodes;
  const games = getUpcomingGames(gamenodes);
  if (games.length > 0) {
    return (
      <Container color={color}>
        <Header color={color} site={site}>
          Upcoming {site === "dawgs" ? "Home " : ""}Games
        </Header>
        <GameList>
          {games.map((game) => {
            const {
              homeTeam,
              awayTeam,
              date,
              competition,
              ticketLink,
              yr,
              rainOut,
              numberSuffix,
              field,
            } = game.frontmatter;
            const isHomeGame = homeTeams.includes(_.toLower(homeTeam));
            const momentDate = moment(date);
            const monthText = moment
              .tz(momentDate, TZ)
              .format("MMMM")
              .toUpperCase();
            const dateText = moment.tz(momentDate, TZ).format("DD");
            const dayText = moment
              .tz(momentDate, TZ)
              .format("ddd h:mma")
              .toUpperCase();

            const opponent =
              teamsByName[
                homeTeams.includes(_.toLower(homeTeam)) ? awayTeam : homeTeam
              ];
            const currentTeam =
              teamsByName[
                homeTeams.includes(_.toLower(homeTeam)) ? homeTeam : awayTeam
              ];

            const fieldName = field
              ? field
              : _.get(
                  teamsByName[homeTeam],
                  "node.frontmatter.location.fieldName"
                );
            const slug = `/schedule/${_.kebabCase(yr)}/${_.kebabCase(
              `${awayTeam}-${homeTeam}`
            )}-${moment(date).tz(TZ).format("YYYY-MM-DD")}${
              numberSuffix ? `-${numberSuffix}` : ""
            }`;

            let leagueObj = allLeagues.find(
              (l) => l.node.frontmatter.name === competition
            );

            const opponentLogo = getImage(
              _.get(opponent, "node.frontmatter.logo")
            );

            const homeTeamName = _.get(
              currentTeam,
              "node.frontmatter.name",
              ""
            );

            return (
              <GameItem key={game.id}>
                <DateContainer>
                  <Date color={color}>{dateText}</Date>
                  <Month color={color}>{monthText}</Month>
                  {["diamondbacks"].includes(site) && (
                    <CompetitionChip competition={leagueObj} />
                  )}
                  {site === "dawgs" && (
                    <Ticket href={ticketLink} target="_blank" color={color}>
                      BUY TICKETS
                    </Ticket>
                  )}
                  {site === "academy" && (
                    <TeamDiv color={color}>{homeTeamName}</TeamDiv>
                  )}
                </DateContainer>
                <ContentContainerLink to={slug}>
                  <OpponentContainer>
                    <VersusContainer>
                      <IconContainer site={site}>
                        {opponentLogo ? (
                          <GatsbyImage
                            alt="logo"
                            image={opponentLogo}
                            objectFit="contain"
                            objectPosition="50% 50%"
                            style={{ height: "48px", width: "auto" }}
                          />
                        ) : (
                          <PlacholderContainer>
                            <img
                              src={logoPlaceholder}
                              alt="placeholder"
                              style={{ width: "100%", height: "auto" }}
                              loading="lazy"
                            />
                          </PlacholderContainer>
                        )}
                      </IconContainer>
                      {/* <Symbol>{isHomeGame ? "VS" : "AT"}</Symbol> */}
                      <DayTime>{rainOut ? "RAIN OUT" : dayText}</DayTime>
                    </VersusContainer>
                    {/* <IconContainer site={site}>
                      {opponentLogo ? (
                        <GatsbyImage
                          alt="logo"
                          image={opponentLogo}
                          objectFit="cover"
                          objectPosition="50% 50%"
                        />
                      ) : (
                    <PlacholderContainer>
                      <img
                        src={logoPlaceholder}
                        alt="placeholder"
                        style={{ width: "100%", height: "auto" }}
                        loading="lazy"
                      />
                    </PlacholderContainer>
                      )}
                    </IconContainer> */}
                    <OpponentInfo>
                      <Town>
                        {
                          _.get(
                            opponent,
                            "node.frontmatter.location.town",
                            ""
                          ).split(",")[0]
                        }
                      </Town>
                      <TeamName>
                        {_.get(opponent, "node.frontmatter.name", "")}
                      </TeamName>
                      <Field>{fieldName}</Field>
                    </OpponentInfo>
                  </OpponentContainer>
                </ContentContainerLink>
              </GameItem>
            );
          })}
        </GameList>
      </Container>
    );
  } else {
    return <div tw="lg:mt-52" />;
  }
};

export default UpcomingGames;

const getUpcomingGames = (games) => {
  const currentDate = moment();

  let upcomingGames = games.filter((game) => {
    const gameDate = moment(game.frontmatter.date);
    return currentDate.isBefore(gameDate);
  });
  // get most recent 4 games
  return upcomingGames.slice(0, 4);
};
